import { z } from "zod";

import stellar from "@/services/stellar";

import { createServerFn } from "@tanstack/react-start";
import { redirect } from "@tanstack/react-router";
import { useAppSession } from "@/lib/serverSession";
import { authSessionSchema, authUserSchema } from "../schemes";
import { LoginError, type LoginErrorCode } from "./LoginError";

const hasErrorField = (value: unknown): value is { error: unknown } =>
	typeof value === "object" && value !== null && "error" in value;

const getErrorCode = (error: unknown): LoginErrorCode => {
	if (hasErrorField(error) && error.error === "The user are disable") {
		return "account_disabled";
	}

	return "invalid_credentials";
};

const loginPayloadSchema = z.object({
	email: z.email(),
	password: z.string(),
});

export const loginFn = createServerFn({ method: "POST" })
	.inputValidator(loginPayloadSchema)
	.handler(async ({ data }) => {
		try {
			const response = await stellar.action("auth.login", data);
			const parsedResponse = authSessionSchema.parse(response);

			// create session and store the token
			const session = await useAppSession();
			await session.update({
				token: parsedResponse.token,
			});

			return {
				token: parsedResponse.token,
				user: parsedResponse.user,
			};
		} catch (error) {
			return {
				error: new LoginError(getErrorCode(error)),
			};
		}
	});

export const logoutFn = createServerFn({ method: "POST" }).handler(async () => {
	// clean the session
	const session = await useAppSession();
	await session.clear();

	// redirect to login page
	throw redirect({ to: "/auth/login" });
});

const checkSessionResponseSchema = z.object({
	user: authUserSchema,
});

export const checkSessionFn = createServerFn().handler(async () => {
	const session = await useAppSession();
	const token = session.data.token;

	if (!token) {
		return {
			error: new LoginError("invalid_credentials"),
		};
	}

	try {
		const response = await stellar.action("auth.checkSession", { token });
		const parsedResponse = checkSessionResponseSchema.parse(response);

		return {
			token,
			user: parsedResponse.user,
		};
	} catch (error) {
		session.clear();

		return {
			error: new LoginError(getErrorCode(error)),
		};
	}
});
